import { Panel } from 'primereact/panel';
import { useCallback, useEffect, useRef, useState, type MouseEventHandler } from 'react';
import { Button, ListBox, ListBoxItem, Popover, Select, SelectValue } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { convertComparisonTypeToString, Input, OdinIcon, OdinIconSize } from '../../../..';
import { ContinuousScrollerComparisonType } from '../../../continuous-scroller/continuous-scroller-types';
import FilterChip from '../common/filter-chip.component';
import type { MobileFilterInterface } from '../data-grid-filter.interface';
import { comparisonDropDownData } from '../data-grid-numeric-filter-panel.component';

type FilterType = {
  comparison: ContinuousScrollerComparisonType;
  value: string | Array<string>;
  customDataProperties?: { [key: string]: unknown };
};

function MobileNumericFilter({ column, filterValue, id, onInputChange }: MobileFilterInterface) {
  const [selectedComparisonValue, setSelectedComparisonValue] = useState<ContinuousScrollerComparisonType>();
  //Used as value when only one input is visible
  const [minimumValue, setMinimumValue] = useState<string>('');
  const [maximumValue, setMaximumValue] = useState<string>('');
  const [maxValueError, setMaxValueError] = useState<string | undefined>(undefined);
  const { t } = useTranslation();

  const parseNumberFilter = useCallback((filter: string | Array<string>, type = 'number') => {
    const getValue = (value: string) => (type === 'number' ? parseInt(value) : parseFloat(value));
    if (Array.isArray(filter)) {
      return filter.map((value) => getValue(value));
    } else {
      return getValue(filter);
    }
  }, []);

  const clearFilter = useCallback(() => {
    // keep comparison type on clear
    const previousComparison = filterValue?.customDataProperties?.originalComparison as
      | ContinuousScrollerComparisonType
      | undefined;
    if (previousComparison != undefined) {
      onInputChange({ column, key: id, value: '', comparison: previousComparison, removeEmpty: false });
    } else {
      onInputChange({ column, key: id });
    }
  }, [column, filterValue, id, onInputChange]);

  const onFilterValueChange = useCallback(
    (filter: FilterType) => {
      if (filter?.comparison === undefined) {
        return;
      }
      const numberType = column.searchType === 'Decimal' ? 'decimal' : undefined;
      onInputChange({
        column,
        key: id,
        value: filter.value ? parseNumberFilter(filter.value as string | string[], numberType) : '',
        comparison: filter.comparison,
        // we need to keep empty filter preserve comparison type
        // otherwise, it's always reset to default (equals)
        removeEmpty: false,
      });
    },
    [column, id, onInputChange, parseNumberFilter]
  );

  const ref = useRef<Panel>(null);
  const showFilterPanel = () => ref.current?.toggle(undefined);

  const displayValue = () => {
    if (selectedComparisonValue === ContinuousScrollerComparisonType.Between) {
      if (maximumValue.length === 0 || minimumValue.length === 0) {
        return '';
      }
      return `${resolveComparisonDisplayValue(selectedComparisonValue)} ${[minimumValue, maximumValue]}`;
    } else if (selectedComparisonValue && minimumValue.length !== 0) {
      return `${resolveComparisonDisplayValue(selectedComparisonValue)} ${minimumValue}`;
    }
  };

  useEffect(() => {
    const comparisonFromFilter = filterValue?.customDataProperties?.originalComparison as
      | ContinuousScrollerComparisonType
      | undefined;
    if (comparisonFromFilter) {
      setSelectedComparisonValue(comparisonFromFilter);
    } else if (column.searchComparison) {
      setSelectedComparisonValue(column.searchComparison);
    }
  }, [filterValue?.customDataProperties?.originalComparison, column.searchComparison]);

  useEffect(() => {
    const currentValue = (filterValue?.value as string | Array<string>) ?? '';
    if (currentValue.length === 0) {
      setMinimumValue('');
      setMaximumValue('');
    } else {
      if (Array.isArray(currentValue) && currentValue.length === 2) {
        setMinimumValue(currentValue[0]);
        setMaximumValue(currentValue[1]);
      } else if (!Array.isArray(currentValue)) {
        if (
          selectedComparisonValue === ContinuousScrollerComparisonType.Between &&
          currentValue.split(',').length === 2
        ) {
          const values = currentValue.split(',');
          setMinimumValue(values[0]);
          setMaximumValue(values[1]);
        } else {
          setMinimumValue(currentValue);
        }
      }
    }
  }, [filterValue?.value, selectedComparisonValue]);

  const applyFilters = (value: string, secondValue: string) => {
    if (
      selectedComparisonValue === undefined ||
      (selectedComparisonValue === ContinuousScrollerComparisonType.Between &&
        (value.length === 0 || secondValue.length === 0))
    ) {
      return;
    }
    const filter: FilterType = {
      customDataProperties: column.customDataProperties,
      value: '',
      comparison: ContinuousScrollerComparisonType.Equals,
    };

    switch (selectedComparisonValue) {
      case ContinuousScrollerComparisonType.Between:
        filter.comparison = ContinuousScrollerComparisonType.Between;
        filter.value = [value, secondValue];
        break;
      case ContinuousScrollerComparisonType.GreaterThanEqualTo:
        filter.comparison = ContinuousScrollerComparisonType.GreaterThanEqualTo;
        filter.value = value;
        break;
      case ContinuousScrollerComparisonType.LessThanEqualTo:
        filter.comparison = ContinuousScrollerComparisonType.LessThanEqualTo;
        filter.value = value;
        break;
      case ContinuousScrollerComparisonType.Equals:
        filter.comparison = ContinuousScrollerComparisonType.Equals;
        filter.value = value;
        break;
    }

    onFilterValueChange(filter);
  };

  const resetFilters = useCallback(() => {
    clearFilter();
    setMaximumValue('');
    setMinimumValue('');
    setMaxValueError(undefined);
  }, [clearFilter]);

  const onMinimumValueChange = (value: string | undefined) => {
    const newMinimumValue = value ?? '';
    if (isValidInput(newMinimumValue, maximumValue)) {
      setMinimumValue(newMinimumValue);
      applyFilters(newMinimumValue, maximumValue ?? '');
    }
  };

  const onMaximumValueChange = (value: string | undefined) => {
    const newMaximumValue = value ?? '';
    if (isValidInput(minimumValue, newMaximumValue)) {
      setMaximumValue(newMaximumValue);
      applyFilters(minimumValue ?? '', newMaximumValue);
    }
  };

  const onComparisonValueChange = (value: ContinuousScrollerComparisonType) => {
    resetFilters();
    setSelectedComparisonValue(value);
    const filter: FilterType = {
      customDataProperties: column.customDataProperties,
      value: '',
      comparison: value,
    };
    onFilterValueChange(filter);
  };

  const isValidInput = (minimumValue?: string, maximumValue?: string) => {
    if (selectedComparisonValue === ContinuousScrollerComparisonType.Between) {
      if (minimumValue?.length === 0 || maximumValue?.length === 0) {
        setMaxValueError(undefined);
        return true;
      }

      const maxVal = parseFloat(maximumValue ?? '');
      const minVal = parseFloat(minimumValue ?? '');
      if (isNaN(maxVal) || isNaN(minVal)) {
        setMaxValueError(undefined);
        return true;
      }

      if (maxVal < minVal) {
        setMaxValueError(t('max-value-error', 'Max value must be greater than or equal to min value'));
        return false;
      } else {
        setMaxValueError(undefined);
      }
    }
    return true;
  };

  const handleClearFilter: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      event.stopPropagation();
      resetFilters();
    },
    [resetFilters]
  );

  return (
    <Panel
      className="odin-flex odin-h-full odin-flex-col odin-w-full odin-cursor-pointer pr-2 odin-border odin-border-gray-4"
      ref={ref}
      header={
        <div onClick={showFilterPanel} className="odin-w-full">
          <FilterChip text={displayValue()} onClear={handleClearFilter} />
        </div>
      }
      collapsed={true}
      toggleable
    >
      <div className="odin-flex odin-h-full odin-flex-col odin-w-full odin-bg-mono-1 mr-2 p-2">
        <Select
          aria-label="Comparison options"
          value={selectedComparisonValue}
          onChange={(selected) => onComparisonValueChange(selected as ContinuousScrollerComparisonType)}
          className="odin-w-full"
        >
          <Button className="prime-select-button odin-w-full odin-flex odin-border odin-border-gray-4 placeholder:odin-rounded-sm odin-cursor-pointer justify-between odin-bg-mono-1 odin-p-1">
            <SelectValue className="odin-text-mono-0 odin-text-sm  odin-flex-1 odin-text-start odin-overflow-hidden odin-whitespace-nowrap odin-text-ellipsis" />
            <span aria-hidden="true" className="odin-pr-2">
              <OdinIcon icon="Filter" size={OdinIconSize.Small} />
            </span>
          </Button>
          <Popover className="prime-select-filter-popover">
            <ListBox selectionMode="single" items={comparisonDropDownData} className="odin-overflow-y-auto">
              {(item) => <ListBoxItem id={item.value as number}>{item.text as string}</ListBoxItem>}
            </ListBox>
          </Popover>
        </Select>
        <div className="odin-flex odin-flex-col odin-gap-3 odin-pt-2">
          <Input
            name="min"
            type="number"
            value={minimumValue}
            placeholder={
              selectedComparisonValue === ContinuousScrollerComparisonType.Between
                ? t('min-value', 'Min value')
                : t('value', 'Value')
            }
            onChange={onMinimumValueChange}
            debounceTime={0}
          />
          {selectedComparisonValue === ContinuousScrollerComparisonType.Between && (
            <Input
              name="max"
              type="number"
              value={maximumValue}
              placeholder={t('max-value', 'Max value')}
              onChange={onMaximumValueChange}
              error={maxValueError}
              debounceTime={0}
            />
          )}
        </div>
      </div>
    </Panel>
  );
}

export default MobileNumericFilter;

function resolveComparisonDisplayValue(value: ContinuousScrollerComparisonType) {
  const appliedFilter = comparisonDropDownData.find((item) => item.value === value);
  if (appliedFilter) {
    return appliedFilter.text;
  }
  return convertComparisonTypeToString(value);
}
